import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { AppText } from '@/components/ui/app-text';
import { colors, fonts, radius, spacing } from '@/theme/tokens';
import { monthCells } from '@/utils/calendar';

/**
 * Tap-to-pick date field with an inline month calendar. Replaces the old
 * "type dd.mm.rrrr" text inputs (users asked for a calendar, and typing also
 * fought the on-screen keyboard hiding the field). Pure RN, works on web +
 * native. Value/onChange speak ISO ("2027-08-15"); null = not set.
 */

const WEEKDAYS_PL = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

function isoToPl(iso: string): string {
  const [y, mo, d] = iso.split('-');
  return `${d}.${mo}.${y}`;
}


interface DateFieldProps {
  label?: string;
  value: string | null; // ISO
  onChange: (iso: string | null) => void;
  placeholder?: string;
  /** Optional: disable days strictly before this ISO date (e.g. today). */
  minIso?: string;
}

export function DateField({ label, value, onChange, placeholder = 'Wybierz datę', minIso }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  // Owner: tapping the "Styczeń 2027" header did nothing — you couldn't jump to
  // a distant month/year. 'month' mode shows a year stepper + 12-month grid.
  const [mode, setMode] = useState<'day' | 'month'>('day');
  const initial = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const cells = monthCells(viewYear, viewMonth);

  const todayIso = new Date().toISOString().slice(0, 10);
  const goPrev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const goNext = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };
  const pick = (day: number) => {
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(iso);
    setOpen(false);
  };

  return (
    <View style={{ gap: spacing.base }}>
      {label ? <AppText variant="labelLg" color={colors.navy}>{label}</AppText> : null}

      <Pressable
        accessibilityRole="button"
        onPress={() => { setMode('day'); setOpen((o) => !o); }}
        style={{
          backgroundColor: colors.card,
          borderRadius: radius.button,
          paddingHorizontal: spacing.md,
          paddingVertical: spacing.sm,
          minHeight: 52,
          flexDirection: 'row',
          alignItems: 'center',
          gap: spacing.sm,
        }}
      >
        <Ionicons name="calendar-outline" size={20} color={colors.navy} />
        <AppText
          variant="bodyLg"
          color={value ? colors.textPrimary : colors.outlineVariant}
          style={{ flex: 1, fontFamily: fonts.semiBold }}
        >
          {value ? isoToPl(value) : placeholder}
        </AppText>
        {value ? (
          <Pressable accessibilityRole="button" onPress={() => onChange(null)} hitSlop={8}>
            <Ionicons name="close-circle" size={20} color={colors.outlineVariant} />
          </Pressable>
        ) : (
          <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.outlineVariant} />
        )}
      </Pressable>

      {open && mode === 'month' && (
        <View style={{ backgroundColor: colors.card, borderRadius: radius.card, padding: spacing.md, gap: spacing.sm }}>
          {/* Year stepper */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Pressable accessibilityRole="button" onPress={() => setViewYear((y) => y - 1)} style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="chevron-back" size={20} color={colors.navy} />
            </Pressable>
            <AppText variant="headlineSm" color={colors.navy}>{viewYear}</AppText>
            <Pressable accessibilityRole="button" onPress={() => setViewYear((y) => y + 1)} style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="chevron-forward" size={20} color={colors.navy} />
            </Pressable>
          </View>
          {/* 12-month grid */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {MONTHS_PL.map((m, i) => {
              const selected = i === viewMonth;
              return (
                <Pressable
                  key={m}
                  accessibilityRole="button"
                  onPress={() => { setViewMonth(i); setMode('day'); }}
                  style={{ width: '33.33%', paddingVertical: spacing.xs, alignItems: 'center' }}
                >
                  <View
                    style={{
                      paddingHorizontal: spacing.sm,
                      paddingVertical: spacing.xs,
                      borderRadius: radius.button,
                      backgroundColor: selected ? colors.navy : 'transparent',
                    }}
                  >
                    <AppText variant="bodyMd" color={selected ? '#FFFFFF' : colors.textPrimary}>
                      {m.slice(0, 3)}
                    </AppText>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}

      {open && mode === 'day' && (
        <View style={{ backgroundColor: colors.card, borderRadius: radius.card, padding: spacing.md, gap: spacing.sm }}>
          {/* Month navigator — the label is now a button that jumps to month/year. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Pressable accessibilityRole="button" onPress={goPrev} style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="chevron-back" size={20} color={colors.navy} />
            </Pressable>
            <Pressable accessibilityRole="button" onPress={() => setMode('month')} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, minHeight: 40, paddingHorizontal: spacing.sm }}>
              <AppText variant="labelLg" color={colors.navy}>{`${MONTHS_PL[viewMonth]} ${viewYear}`}</AppText>
              <Ionicons name="chevron-down" size={16} color={colors.navy} />
            </Pressable>
            <Pressable accessibilityRole="button" onPress={goNext} style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="chevron-forward" size={20} color={colors.navy} />
            </Pressable>
          </View>

          {/* Weekday header */}
          <View style={{ flexDirection: 'row' }}>
            {WEEKDAYS_PL.map((w) => (
              <AppText key={w} variant="labelMd" style={{ flex: 1, textAlign: 'center' }} color={colors.outlineVariant}>
                {w}
              </AppText>
            ))}
          </View>

          {/* Day grid — 7 columns */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {cells.map((day, idx) => {
              if (day === null) return <View key={`e${idx}`} style={{ width: `${100 / 7}%`, height: 40 }} />;
              const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const selected = value === iso;
              const isToday = todayIso === iso;
              const disabled = minIso ? iso < minIso : false;
              return (
                <Pressable
                  key={iso}
                  accessibilityRole="button"
                  disabled={disabled}
                  onPress={() => pick(day)}
                  style={{ width: `${100 / 7}%`, height: 40, alignItems: 'center', justifyContent: 'center' }}
                >
                  <View
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: radius.full,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: selected ? colors.navy : 'transparent',
                      borderWidth: isToday && !selected ? 1 : 0,
                      borderColor: colors.primary,
                      opacity: disabled ? 0.3 : 1,
                    }}
                  >
                    <AppText variant="bodyMd" color={selected ? '#FFFFFF' : colors.textPrimary}>
                      {day}
                    </AppText>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
}
